/**
 * Media Realms Page
 * 
 * Manage RTP port ranges for media handling.
 * Refactored to use common components for consistent UI patterns.
 */

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Radio,
  Plus,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { ConfigurationMediaRealm } from '@sbclite/core';
import { api } from '../../services/api';
import { useMediaRealms, useDeleteMediaRealm, queryKeys } from '../../hooks/useConfigurationApi';

// Common components
import {
  PageHeader,
  PageContainer,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemCard,
  ItemCardIcon,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  DeleteConfirmModal,
  Modal,
} from '../../components/common';
import { useViewMode } from '../../hooks/useViewMode';

type MediaRealmInfo = ConfigurationMediaRealm;

// ========================================
// CONSTANTS
// ========================================


// ========================================
// MAIN COMPONENT
// ========================================

export default function MediaRealmsPage() {
  const queryClient = useQueryClient();
  
  // State management - use user preference for default view mode
  const [viewMode, setViewMode] = useViewMode();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRealm, setSelectedRealm] = useState<MediaRealmInfo | null>(null);
  const [realmToDelete, setRealmToDelete] = useState<MediaRealmInfo | null>(null);
  const [editingRealm, setEditingRealm] = useState<MediaRealmInfo | null>(null);

  // Use shared media realms hook (same cache as SIP Peer wizard so new realms appear there)
  const { data: realms = [], isLoading: realmsQueryLoading, isFetching: realmsQueryFetching, error: realmsQueryError, refetch: refetchRealms } = useMediaRealms();
  const deleteMutation = useDeleteMediaRealm();

  const handleDeleteSuccess = () => {
    setShowDeleteModal(false);
    setRealmToDelete(null);
  };

  const filteredRealms = realms.filter(realm =>
    searchQuery === '' || realm.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedRealms = [...filteredRealms].sort((a, b) => a.name.localeCompare(b.name));

  const stats = { total: realms.length };

  // Loading state
  if (realmsQueryLoading) {
    return <LoadingState />;
  }

  // Error state
  if (realmsQueryError) {
    return (
      <ErrorState
        title="Failed to load media realms"
        message={realmsQueryError instanceof Error ? realmsQueryError.message : 'Unknown error'}
        onRetry={() => refetchRealms()}
      />
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader
        title="Media Realms"
        description="Manage RTP port ranges for media handling"
        onRefresh={() => refetchRealms()}
        isRefreshing={realmsQueryFetching}
        primaryAction={{
          label: 'Create Media Realm',
          icon: Plus,
          onClick: () => {
            setEditingRealm(null);
            setShowCreateModal(true);
          },
        }}
      />

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Realms"
          value={stats.total}
          icon={Radio}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
      </StatsGrid>

      <FilterBar
        searchPlaceholder="Search media realms..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={[]}
        filterValue=""
        onFilterChange={() => {}}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Media Realms Display */}
      {sortedRealms.length === 0 ? (
        <EmptyState
          icon={Radio}
          title="No media realms found"
          description="Get started by creating a media realm."
          isFiltered={searchQuery !== ''}
          action={{
            label: 'Create Media Realm',
            icon: Plus,
            onClick: () => setShowCreateModal(true),
          }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedRealms.map((realm) => (
            <ItemCard
              key={realm.id}
              icon={
                <ItemCardIcon 
                  icon={<Radio className="w-4 h-4" />} 
                  bgColor="bg-blue-100" 
                />
              }
              title={realm.name}
              details={[
                { label: 'Port Range', value: `${realm.portRangeStart} - ${realm.portRangeEnd}` },
                { label: 'Anchor Address', value: realm.anchorAddress || 'Not set' },
              ]}
              actions={
                <ItemActions
                  variant="card"
                  actions={[
                    {
                      kind: 'view',
                      onClick: () => {
                        setSelectedRealm(realm);
                        setShowDetailsModal(true);
                      },
                    },
                    {
                      kind: 'edit',
                      onClick: () => {
                        setEditingRealm(realm);
                        setShowCreateModal(true);
                      },
                    },
                    {
                      kind: 'delete',
                      onClick: () => {
                        setRealmToDelete(realm);
                        setShowDeleteModal(true);
                      },
                    },
                  ]}
                />
              }
            />
          ))}
        </div>
      ) : (
        // List View (Table)
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Realm</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Port Range</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedRealms.map((realm) => (
                <tr key={realm.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{realm.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {realm.portRangeStart} - {realm.portRangeEnd}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <ItemActions
                      variant="table"
                      actions={[
                        {
                          kind: 'view',
                          onClick: () => {
                            setSelectedRealm(realm);
                            setShowDetailsModal(true);
                          },
                        },
                        {
                          kind: 'edit',
                          onClick: () => {
                            setEditingRealm(realm);
                            setShowCreateModal(true);
                          },
                        },
                        {
                          kind: 'delete',
                          onClick: () => {
                            setRealmToDelete(realm);
                            setShowDeleteModal(true);
                          },
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreateEditMediaRealmModal
          realm={editingRealm}
          existingRealms={realms}
          onClose={() => {
            setShowCreateModal(false);
            setEditingRealm(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.mediaRealms });
            setShowCreateModal(false);
            setEditingRealm(null);
          }}
        />
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRealm && (
        <MediaRealmDetailsModal
          realm={selectedRealm}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRealm(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {realmToDelete && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setRealmToDelete(null);
          }}
          onConfirm={() => realmToDelete && deleteMutation.mutate(realmToDelete.id, { onSuccess: handleDeleteSuccess })}
          title="Delete Media Realm"
          entityType="media realm"
          entityName={realmToDelete.name}
          additionalInfo={
            <>
              <p className="text-sm text-gray-600 mb-1 mt-2">Port range:</p>
              <p className="text-sm text-gray-700">
                {realmToDelete.portRangeStart} - {realmToDelete.portRangeEnd}
              </p>
            </>
          }
          isDeleting={deleteMutation.isPending}
        />
      )}
    </PageContainer>
  );
}

// ========================================
// CREATE/EDIT MODAL
// ========================================

interface CreateEditMediaRealmModalProps {
  realm: MediaRealmInfo | null;
  existingRealms: MediaRealmInfo[];
  onClose: () => void;
  onSuccess: () => void;
}

const mediaRealmFormSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
    anchorAddress: z.string().min(1, 'Anchor address is required'),
    portRangeStart: z.number({ error: 'Start port is required' }).int().min(1024).max(65535),
    portRangeEnd: z.number({ error: 'End port is required' }).int().min(1024).max(65535),
  })
  .refine((d) => d.portRangeEnd > d.portRangeStart, {
    message: 'End port must be greater than start port',
    path: ['portRangeEnd'],
  });

type MediaRealmFormData = z.infer<typeof mediaRealmFormSchema>;

const CreateEditMediaRealmModal: React.FC<CreateEditMediaRealmModalProps> = ({
  realm,
  existingRealms,
  onClose,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<MediaRealmFormData>({
    resolver: zodResolver(mediaRealmFormSchema),
    defaultValues: {
      name: realm?.name ?? '',
      description: realm?.description ?? '',
      anchorAddress: realm?.anchorAddress ?? '',
      portRangeStart: realm?.portRangeStart ?? 16384,
      portRangeEnd: realm?.portRangeEnd ?? 32768,
    },
  });

  const watchStart = form.watch('portRangeStart');
  const watchEnd = form.watch('portRangeEnd');

  // Non-blocking warning: detect overlaps with other existing realms (excluding self).
  const overlappingRealms = existingRealms.filter((other) => {
    if (other.id === realm?.id) return false;
    const start = Number(watchStart);
    const end = Number(watchEnd);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    // Two inclusive ranges [start, end] and [s2, e2] overlap iff start <= e2 && s2 <= end.
    return start <= other.portRangeEnd && other.portRangeStart <= end;
  });
  const hasOverlap = overlappingRealms.length > 0;

  const onSubmit = async (data: MediaRealmFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const realmData = {
        name: data.name,
        description: data.description || undefined,
        anchorAddress: data.anchorAddress,
        portRangeStart: data.portRangeStart,
        portRangeEnd: data.portRangeEnd,
      };

      if (realm) {
        // For updates, use UUID id in the URL path
        await api.put(`/api/media-realms/${realm.id}`, realmData);
      } else {
        await api.post('/api/media-realms', realmData);
      }

      onSuccess();
    } catch (err) {
      // Handle ApiRequestError with response payload (fetch client attaches .response)
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = e.response?.data?.message || e.message || 'Failed to save media realm';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={realm ? 'Edit Media Realm' : 'Create Media Realm'}
      subtitle="Configure RTP settings"
      size="lg"
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            {...form.register('name')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Teams Media Realm"
          />
          {form.formState.errors.name && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            {...form.register('description')}
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Anchor Address</label>
          <input
            type="text"
            {...form.register('anchorAddress')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Media anchor IP or host"
          />
          {form.formState.errors.anchorAddress && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.anchorAddress.message}</p>
          )}
        </div>

        {/* Port Range */}
        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-900 mb-3">RTP Port Range</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Port</label>
              <input
                type="number"
                {...form.register('portRangeStart', { valueAsNumber: true })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {form.formState.errors.portRangeStart && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.portRangeStart.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Port</label>
              <input
                type="number"
                {...form.register('portRangeEnd', { valueAsNumber: true })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {form.formState.errors.portRangeEnd && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.portRangeEnd.message}</p>
              )}
            </div>
          </div>

          {hasOverlap && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              <span>
                Warning: this port range overlaps with{' '}
                {overlappingRealms.map((r) => r.name).join(', ')}. Overlapping ranges can cause
                port-allocation conflicts.
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                {realm ? 'Update' : 'Create'}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ========================================
// DETAILS MODAL
// ========================================

interface MediaRealmDetailsModalProps {
  realm: MediaRealmInfo;
  onClose: () => void;
}

const MediaRealmDetailsModal: React.FC<MediaRealmDetailsModalProps> = ({
  realm,
  onClose
}) => {
  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={realm.name}
      subtitle="Media Realm Details"
      size="lg"
      footer={
        <div className="flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Basic Info */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            {realm.description && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Description</span>
                <span className="text-sm text-gray-900">{realm.description}</span>
              </div>
            )}
            {!realm.description && <span className="text-sm text-gray-500">No description</span>}
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Anchor Address</span>
              <span className="text-sm font-mono text-gray-900">{realm.anchorAddress || 'Not set'}</span>
            </div>
          </div>
        </div>

        {/* RTP Port Range */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">RTP Port Range</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Port Range</span>
              <span className="text-sm font-mono text-gray-900">
                {realm.portRangeStart} - {realm.portRangeEnd}
              </span>
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-sm text-gray-600">Available Ports</span>
              <span className="text-sm text-gray-900">
                {realm.portRangeEnd - realm.portRangeStart + 1} ports
              </span>
            </div>
          </div>
        </div>

      </div>
    </Modal>
  );
};
